
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, UserPlus, Download } from "lucide-react";
import { deleteStudent } from "@/hooks/useSupabaseData";
import { StudentForm } from "./StudentForm";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { useToast } from "@/hooks/use-toast";
import { StudentCard } from "./StudentCard";
import { useStudentsList } from "@/hooks/useStudentsList";
import { useExportData } from "@/hooks/useExportData";
import { StudentSkeletonCard } from "./student/StudentSkeletonCard";

export const StudentList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<any>(null);
  const { students, isLoading, refetch } = useStudentsList(searchTerm);
  const { exportStudents, isExporting } = useExportData();
  const { toast } = useToast();

  const handleEditStudent = (student: any) => {
    setSelectedStudent(student);
    setShowStudentForm(true);
  };

  const handleDeleteStudent = (student: any) => {
    setStudentToDelete(student);
    setShowDeleteDialog(true);
  };

  const confirmDeleteStudent = async () => {
    if (!studentToDelete) return;

    try {
      await deleteStudent(studentToDelete.id);
      toast({
        title: "Estudiante eliminado",
        description: `${studentToDelete.name} ha sido eliminado exitosamente.`
      });
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el estudiante.",
        variant: "destructive"
      });
    } finally {
      setShowDeleteDialog(false);
      setStudentToDelete(null);
    }
  };

  const handleStudentFormSuccess = () => {
    refetch();
    setSelectedStudent(null);
    setShowStudentForm(false);
  };

  return (
    <>
      <div className="space-y-6">
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Lista de Estudiantes ({students.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre o clase..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    exportStudents();
                  }}
                  variant="outline"
                  disabled={isExporting || students.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isExporting ? 'Exportando...' : 'Exportar estudiantes'}
                </Button>
                <Button onClick={() => setShowStudentForm(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuevo Alumno
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, index) => (
                  <StudentSkeletonCard key={index} />
                ))}
              </div>
            ) : students.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {students.map((student: any) => (
                  <StudentListItem 
                    key={student.id} 
                    student={student} 
                    onEdit={handleEditStudent}
                    onDelete={handleDeleteStudent}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No se encontraron estudiantes.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <StudentForm
        open={showStudentForm}
        onOpenChange={setShowStudentForm}
        student={selectedStudent}
        onSuccess={handleStudentFormSuccess}
      />
      
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={confirmDeleteStudent}
        title="Eliminar estudiante"
        description={`¿Seguro que quieres eliminar a ${studentToDelete?.name}? Esta acción no se puede deshacer.`}
      />
    </>
  );
};

// Separating the individual student item into its own component to optimize rendering
const StudentListItem = ({ student, onEdit, onDelete }: { 
  student: any; 
  onEdit: (student: any) => void; 
  onDelete: (student: any) => void;
}) => {
  const { toast } = useToast();
  
  return (
    <StudentCard
      student={student}
      onEdit={onEdit}
      onDelete={onDelete}
      onViewHistory={() => {
        // TODO: Implementar modal o navegación a historial
        toast({
          title: "Historial",
          description: `Mostrando historial de ${student.name}`
        });
      }}
    />
  );
};
